import { useState, useMemo } from "react";

export type WeightUnit = "kg" | "lbs";

/**
 * Exact conversion constant.
 * 1 kilogram = 2.2046226218487757 pounds
 */
export const KG_TO_LBS = 2.2046226218487757;

/** Pure conversion helpers */
export function kgToLbs(kg: number): number {
    return kg * KG_TO_LBS;
}
export function lbsToKg(lbs: number): number {
    return lbs / KG_TO_LBS;
}

/** Format a numeric value for a UI display with fixed precision (returns '' for null/NaN) */
export function formatWeight(value: number | null | undefined, precision = 1): string {
    if (value === null || value === undefined) return "";
    const n = Number(value);
    if (Number.isNaN(n)) return "";
    return n.toFixed(precision);
}

/**
 * Hook that centralizes unit state + conversion helpers and formatting preferences.
 *
 * Returns:
 * - unit: current unit ("kg" | "lbs")
 * - setUnit: setter for unit
 * - toDisplay(kg): format a stored kg value for editing in the currently selected unit
 * - parseToKg(display): parse a user-entered display value (string|number) according to current unit and return canonical kg (number) or null if invalid/empty
 * - helpers: pure conversion functions & formatting helpers
 */
export default function useWeightConversion(initialUnit: WeightUnit = "kg", opts?: {
    displayPrecisionKg?: number;   // decimals to show when displaying kg
    displayPrecisionLbs?: number;  // decimals to show when displaying lbs
    storagePrecisionKg?: number;   // decimals to round when storing kg (optional)
}) {
    const [unit, setUnit] = useState<WeightUnit>(initialUnit);

    const displayPrecisionKg = opts?.displayPrecisionKg ?? 1;
    const displayPrecisionLbs = opts?.displayPrecisionLbs ?? 1;
    const storagePrecisionKg = opts?.storagePrecisionKg ?? 3;

    const toDisplay = useMemo(() => {
        return (kg: number | null | undefined): string => {
            if (kg === null || kg === undefined) return "";
            if (unit === "lbs") {
                return (kgToLbs(kg)).toFixed(displayPrecisionLbs);
            }
            return Number(kg).toFixed(displayPrecisionKg);
        };
    }, [unit, displayPrecisionKg, displayPrecisionLbs]);

    const parseToKg = (displayValue: string | number | null | undefined): number | null => {
        if (displayValue === null || displayValue === undefined) return null;
        const raw = typeof displayValue === "number" ? displayValue : displayValue.trim();
        if (raw === "") return null;
        const parsed = Number(raw);
        if (Number.isNaN(parsed)) return null;
        let kgVal = unit === "lbs" ? lbsToKg(parsed) : parsed;
        // round for storage to storagePrecisionKg decimals
        const factor = Math.pow(10, storagePrecisionKg);
        kgVal = Math.round(kgVal * factor) / factor;
        return kgVal;
    };

    return {
        unit,
        setUnit,
        toDisplay,      // (kg) => string formatted for input
        parseToKg,      // (display) => kg number | null
        // expose pure helpers for callers that prefer direct usage
        helpers: {
            kgToLbs,
            lbsToKg,
            formatWeight,
        }
    } as const;
}
